import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFetcher, useRevalidator } from "react-router-dom";
import { toast } from "sonner";
import { MarkdownRenderer } from "../ui";

// A table wider than its panel scrolls between these two: the row's name on the left and its
// actions on the right stay in view.
const STICKY_FIRST = "sticky left-0 z-10 bg-background";
const STICKY_ACTIONS = "sticky right-0 z-10 bg-background";

export type EditableRelatedListColumn<T> = {
    name: string;
    accessorKey?: keyof T;
    accessorFn?: (item: T) => React.ReactNode;
    cellFn?: (item: T) => React.ReactNode;
    size?: number;
};

export type EditableRelatedListFormProps<T, TContext = undefined> = {
    item: T | undefined;
    setOpen: (open: boolean) => void;
    context: TContext;
};

export type EditableRelatedListProps<T extends { id: string | number }, TContext = undefined> = {
    title: string;
    description?: string;
    items: T[];
    columns: EditableRelatedListColumn<T>[];
    formComponent: React.FC<EditableRelatedListFormProps<T, TContext>>;
    context?: TContext;
    actionPrefix: string;
    extraDeleteData?: Record<string, string>;
    itemToId?: (item: T) => string;
    createDialogTitle?: string;
    editDialogTitle?: string;
    deleteConfirmMessage?: string;
    permissions?: {
        create: boolean;
        update: boolean;
        delete: boolean;
    };
    emptyMessage?: string;
    onDeleteSuccess?: () => void;
};

export function EditableRelatedList<T extends { id: string | number }, TContext = undefined>({
    title,
    description,
    items,
    columns,
    formComponent,
    context,
    actionPrefix,
    extraDeleteData,
    itemToId = item => String(item.id),
    createDialogTitle,
    editDialogTitle,
    deleteConfirmMessage,
    permissions,
    emptyMessage = "No items",
    onDeleteSuccess
}: EditableRelatedListProps<T, TContext>) {
    const FormComponent = formComponent;
    const { revalidate } = useRevalidator();
    const deleteFetcher = useFetcher();

    const [createOpen, setCreateOpen] = useState(false);
    const [editItem, setEditItem] = useState<T | null>(null);
    const [deleteItem, setDeleteItem] = useState<T | null>(null);

    const processedDeleteDataRef = useRef<unknown>(null);

    useEffect(() => {
        if (!deleteFetcher.data || deleteFetcher.data === processedDeleteDataRef.current) return;
        processedDeleteDataRef.current = deleteFetcher.data;
        if ((deleteFetcher.data as { success?: boolean }).success) {
            toast.success("Item deleted");
            revalidate();
            onDeleteSuccess?.();
        }
    }, [deleteFetcher.data, revalidate, onDeleteSuccess]);

    const handleEditClose = (open: boolean) => {
        if (!open) {
            setEditItem(null);
        }
    };

    const handleDeleteConfirm = () => {
        if (!deleteItem) return;
        deleteFetcher.submit({ id: itemToId(deleteItem), ...extraDeleteData }, { method: "post", encType: "application/json", action: `?action=delete-${actionPrefix}` });
        setDeleteItem(null);
    };

    return (
        <div className="space-y-4 py-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="max-w-[90%] space-y-1">
                    <h3 className="text-md font-semibold">
                        {title} ({items.length})
                    </h3>
                    {description && <MarkdownRenderer content={description} className="text-xs text-muted-foreground" />}
                </div>
                {permissions?.create && (
                    <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
                        <PlusIcon className="mr-1 h-4 w-4" />
                        Add
                    </Button>
                )}
            </div>

            {/* Table or empty state */}
            {items.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center">
                    <p className="text-sm text-muted-foreground">{emptyMessage}</p>
                    {permissions?.create && (
                        <Button size="sm" variant="outline" className="mt-3" onClick={() => setCreateOpen(true)}>
                            <PlusIcon className="mr-1 h-4 w-4" />
                            Add first item
                        </Button>
                    )}
                </div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((col, i) => (
                                <TableHead key={i} className={cn(i === 0 && STICKY_FIRST)} style={col.size ? { width: col.size } : undefined}>
                                    {col.name}
                                </TableHead>
                            ))}
                            {(permissions?.update || permissions?.delete) && <TableHead className={cn("w-[100px] text-right", STICKY_ACTIONS)} />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map(item => (
                            <TableRow key={itemToId(item)}>
                                {columns.map((col, i) => (
                                    <TableCell key={i} className={cn(i === 0 && STICKY_FIRST)}>
                                        {col.cellFn ? col.cellFn(item) : col.accessorFn ? col.accessorFn(item) : col.accessorKey ? String(item[col.accessorKey] ?? "") : null}
                                    </TableCell>
                                ))}
                                {(permissions?.update || permissions?.delete) && (
                                    <TableCell className={cn("text-right", STICKY_ACTIONS)}>
                                        <div className="flex items-center justify-end gap-1">
                                            {permissions?.update && (
                                                <Button size="sm" variant="ghost" onClick={() => setEditItem(item)}>
                                                    <PencilIcon className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                            {permissions?.delete && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="text-destructive hover:text-destructive"
                                                    onClick={() => setDeleteItem(item)}
                                                    disabled={deleteFetcher.state !== "idle"}
                                                >
                                                    <Trash2Icon className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Create Dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>{createDialogTitle ?? `Add ${title}`}</DialogTitle>
                    </DialogHeader>
                    <FormComponent item={undefined} setOpen={setCreateOpen} context={context as TContext} />
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editItem !== null} onOpenChange={open => !open && handleEditClose(false)}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>{editDialogTitle ?? `Edit ${title}`}</DialogTitle>
                    </DialogHeader>
                    {editItem !== null && <FormComponent item={editItem} setOpen={open => !open && handleEditClose(false)} context={context as TContext} />}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteItem !== null} onOpenChange={open => !open && setDeleteItem(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm deletion</AlertDialogTitle>
                        <AlertDialogDescription>{deleteConfirmMessage ?? "This action cannot be undone. This item will be permanently deleted."}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={handleDeleteConfirm}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
